import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Search, X } from "lucide-react";
import MarketingNav from "@/components/MarketingNav";
import MarketingFooter from "@/components/MarketingFooter";
import ProjectCard from "@/components/ProjectCard";
import { api } from "@/lib/api";
import { FORMATS, GENRES, STAGES, COUNTRIES } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";

export default function Explore() {
  const { t } = useTranslation();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [format, setFormat] = useState("all");
  const [genre, setGenre] = useState("all");
  const [country, setCountry] = useState("all");
  const [stage, setStage] = useState("all");
  const [seeks, setSeeks] = useState("all");

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (format !== "all") params.set("format", format);
      if (genre !== "all") params.set("genre", genre);
      if (country !== "all") params.set("country", country);
      if (stage !== "all") params.set("stage", stage);
      if (seeks !== "all") params.set("seeks", seeks);
      const { data } = await api.get(`/api/projects?${params.toString()}`);
      setProjects(data);
    } finally {
      setLoading(false);
    }
  }, [q, format, genre, country, stage, seeks]);

  useEffect(() => {
    fetchProjects();
  }, [format, genre, country, stage, seeks, fetchProjects]);

  const onSubmit = (e) => {
    e.preventDefault();
    fetchProjects();
  };

  const clear = () => {
    setQ(""); setFormat("all"); setGenre("all"); setCountry("all"); setStage("all"); setSeeks("all");
  };

  return (
    <div className="marketing-shell">
      <MarketingNav />
      <section className="mx-auto max-w-7xl px-6 pt-16 pb-8">
        <p className="eyebrow text-gold">{t("explore.eyebrow")}</p>
        <h1 className="font-display text-5xl md:text-6xl tracking-tight text-white mt-4">
          {t("explore.title_a")} <span className="italic text-gold">{t("explore.title_b")}</span>
        </h1>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-6">
        <form onSubmit={onSubmit} className="glass-card rounded-2xl p-4 md:p-5 flex flex-col md:flex-row gap-3 items-stretch">
          <div className="flex items-center flex-1 bg-white/5 rounded-xl px-4 border border-white/10">
            <Search size={16} className="text-white/50" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("explore.search_placeholder")}
              data-testid="input-explore-search"
              className="bg-transparent border-0 text-white placeholder:text-white/40 focus-visible:ring-0"
            />
          </div>
          <Button type="submit" data-testid="button-explore-search" className="bg-gold text-petroleum hover:bg-gold/90 rounded-xl">
            {t("explore.search_cta")}
          </Button>
        </form>

        <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3">
          <FilterSelect value={format} onChange={setFormat} placeholder={t("explore.filter_format")} allLabel={t("explore.filter_all")} testid="select-format" options={FORMATS} />
          <FilterSelect value={genre} onChange={setGenre} placeholder={t("explore.filter_genre")} allLabel={t("explore.filter_all")} testid="select-genre" options={GENRES.map((g) => ({ value: g, label: g }))} />
          <FilterSelect value={country} onChange={setCountry} placeholder={t("explore.filter_country")} allLabel={t("explore.filter_all")} testid="select-country" options={COUNTRIES.map((c) => ({ value: c, label: c }))} />
          <FilterSelect value={stage} onChange={setStage} placeholder={t("explore.filter_stage")} allLabel={t("explore.filter_all")} testid="select-stage" options={STAGES} />
          <FilterSelect
            value={seeks}
            onChange={setSeeks}
            placeholder={t("explore.filter_seeks")}
            allLabel={t("explore.filter_all")}
            testid="select-seeks"
            options={[
              { value: "placement", label: t("explore.seek_placement") },
              { value: "sponsorship", label: t("explore.seek_sponsorship") },
              { value: "cultural", label: t("explore.seek_cultural") },
            ]}
          />
        </div>

        <div className="mt-3 text-sm text-white/60 flex items-center gap-3">
          {(q || format !== "all" || genre !== "all" || country !== "all" || stage !== "all" || seeks !== "all") && (
            <button onClick={clear} data-testid="button-clear-filters" className="inline-flex items-center gap-1 hover:text-white">
              <X size={12} /> {t("explore.clear_filters")}
            </button>
          )}
          <span>{t("explore.count", { count: projects.length })}</span>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-24">
        {loading ? (
          <div className="text-white/60 text-sm py-12 text-center">{t("common.loading")}</div>
        ) : projects.length === 0 ? (
          <div className="text-white/60 text-center py-20 border border-white/10 rounded-2xl">
            {t("explore.empty")}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((p) => <ProjectCard key={p.id} project={p} theme="dark" />)}
          </div>
        )}
      </section>

      <MarketingFooter />
    </div>
  );
}

function FilterSelect({ value, onChange, placeholder, options, testid, allLabel }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="bg-white/5 border-white/10 text-white h-11 rounded-xl">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{allLabel}</SelectItem>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
